import { __ } from '@wordpress/i18n';
import { registerBlockType } from '@wordpress/blocks';
import { useBlockProps, RichText, InspectorControls } from '@wordpress/block-editor';
import { 
    PanelBody, 
    TabPanel, 
    Button,
    TextControl, 
    ToggleControl, 
    SelectControl
} from '@wordpress/components';
import { useState } from '@wordpress/element';
import './style.scss';
import './editor.scss';
import Repeater from '../../components/repeater';
import VisibilityControl from '../../components/VisibilityControl';
import Typography from '../../components/Typography';
import IconPicker from '../../components/IconPicker';
import Spacing from '../../components/Spacing';
import Border from '../../components/Border';
import { getSpacingStyles, getBorderStyles } from '../../utils/styles';
import { Placeholder } from '@wordpress/components';

registerBlockType('kanews/bibliography', {
    apiVersion: 2,
    title: __('Kaynakça', 'kanews'),
    category: 'kanews',
    icon: 'book-alt',
    supports: {
        html: false,
        align: ['wide', 'full'],
    },
    attributes: {
        sources: {
            type: 'array',
            default: [],
        },
        linkTypography: {
            type: 'object',
            default: {}
        },
        margin: {
            type: 'object',
            default: {}
        },
        padding: {
            type: 'object',
            default: {}
        },
        border: {
            type: 'object',
            default: {}
        },
        visibility: {
            type: 'object',
            default: {
                desktop: false,
                tablet: false,
                mobile: false
            }
        }
    },
    edit: ({ attributes, setAttributes }) => {
        const {
            sources,
            linkTypography,
            margin,
            padding,
            border,
            visibility,
        } = attributes;

        const visibilityClasses = [
            visibility?.desktop ? 'hidden-desktop' : '',
            visibility?.tablet ? 'hidden-tablet' : '',
            visibility?.mobile ? 'hidden-mobile' : ''
        ].filter(Boolean);

        const blockProps = useBlockProps();

        const wrapperProps = {
            className: ['kanews-post-sources', 'kanews-block-margin', ...visibilityClasses].filter(Boolean).join(' '),
            style: {
                ...getSpacingStyles(margin, 'margin')
            }
        };

        const repeaterFields = [
            {
                name: 'text',
                label: __('Kaynak Metni', 'kanews'),
                type: 'text',
                placeholder: __('Kaynak metni...', 'kanews')
            },
            {
                name: 'url',
                label: __('Kaynak Linki', 'kanews'),
                type: 'text',
                placeholder: __('https://...', 'kanews')
            },
            {
                name: 'icon',
                label: __('Kaynak İkonu', 'kanews'),
                type: 'icon',
                component: IconPicker
            }
        ];

        const tabs = [
            {
                name: 'content',
                title: __('İçerik', 'kanews'),
                className: 'content-tab'
            },
            {
                name: 'design',
                title: __('Tasarım', 'kanews'),
                className: 'design-tab'
            },
            {
                name: 'visibility',
                title: __('Görünürlük', 'kanews'),
                className: 'visibility-tab'
            }
        ];

        return (
            <>
            <InspectorControls>
                <TabPanel
                    className="kanews-tab-panel"
                    tabs={tabs}
                >
                    {(tab) => {
                        return (
                            <>
                                {tab.name === 'content' && (
                                    <PanelBody>
                                        <Repeater
                                            title={__('Kaynaklar', 'kanews')}
                                            fields={repeaterFields}
                                            items={sources}
                                            onAdd={() => {
                                                const newSources = [...sources, { id: Date.now(), text: 'Kanews', url: '', icon: 'icon-logout' }];
                                                setAttributes({ sources: newSources });
                                            }}
                                            onRemove={(index) => {
                                                const newSources = [...sources];
                                                newSources.splice(index, 1);
                                                setAttributes({ sources: newSources });
                                            }}
                                            onChange={(index, attribute, value) => {
                                                const newSources = [...sources];
                                                newSources[index] = {
                                                    ...newSources[index],
                                                    [attribute]: value
                                                };
                                                setAttributes({ sources: newSources });
                                            }}
                                            addButtonText={__('Kaynak Ekle', 'kanews')}
                                            emptyMessage={__('Henüz kaynak eklenmemiş', 'kanews')}
                                        />
                                    </PanelBody>
                                )}
                                {tab.name === 'design' && (
                                    <>
                                        <PanelBody title={__('Tipografi', 'kanews')}>
                                            <Typography
                                                label={__('Bağlantı Tipografisi', 'kanews')}
                                                value={linkTypography}
                                                onChange={(value) => setAttributes({ linkTypography: value })}
                                            />
                                        </PanelBody>
                                        <PanelBody title={__('Kenarlık', 'kanews')} initialOpen={false}>
                                            <Border
                                                label={__('Kenarlık', 'kanews')}
                                                value={border}
                                                onChange={(value) => setAttributes({ border: value })}
                                            />
                                        </PanelBody>
                                        <PanelBody title={__('Boşluklar', 'kanews')} initialOpen={false}>
                                            <Spacing
                                                label={__('Dış Boşluk', 'kanews')}
                                                value={margin}
                                                onChange={(value) => setAttributes({ margin: value })}
                                            />
                                            <Spacing
                                                label={__('İç Boşluk', 'kanews')}
                                                value={padding}
                                                onChange={(value) => setAttributes({ padding: value })}
                                            />
                                        </PanelBody>
                                    </>
                                )}
                                {tab.name === 'visibility' && (
                                    <PanelBody>
                                        <VisibilityControl
                                            value={visibility}
                                            onChange={(value) => setAttributes({ visibility: value })}
                                        />
                                    </PanelBody>
                                )}
                            </>
                        );
                    }}
                </TabPanel>
            </InspectorControls>
 
            <div {...blockProps}>
                <div {...wrapperProps}>
                    {sources.map((source, index) => 
                        source.url ? (
                            <div key={index} className='kanews-post-source d-flex align-items-center kanews-block-border' style={{...getBorderStyles(border, 'border')}}>
                                <a
                                    href={source.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={'kanews-source-link kanews-block-padding'}
                                    style={{...linkTypography, ...getSpacingStyles(padding, 'padding')}}
                                >
                                    {source.icon && <i className={source.icon}></i>}
                                    <span className="kanews-source-text">{source.text}</span>
                                </a>
                            </div>
                        ) : (
                            <div key={index} className='kanews-post-source d-flex align-items-center kanews-block-border' style={{...getBorderStyles(border, 'border')}}>
                                <div className='kanews-source-link kanews-block-padding' style={{...linkTypography, ...getSpacingStyles(padding, 'padding')}}>
                                    {source.icon && <i className={source.icon}></i>}
                                    <span className="kanews-source-text">{source.text}</span>
                                </div>
                            </div>
                        )
                    )}
                    {sources.length === 0 && (
                        <Placeholder
                            icon="book-alt"
                            label={__('Kaynakça', 'kanews')}
                            instructions={__('Kaynakça içeriğini buraya ekleyin.', 'kanews')}
                         >
                            <Button
                                isPrimary
                                onClick={() => setAttributes({ sources: [{ id: Date.now(), text: 'Kanews', url: '', icon: 'icon-logout' }] })}
                            >
                                {__('Kaynak Ekle', 'kanews')}
                            </Button>
                        </Placeholder>
                    )}
                </div>
            </div>
             
            </>
        );
    },
    save: ({ attributes }) => {
        const {
            sources,
            linkTypography,
            margin,
            padding,
            border,
            visibility,
        } = attributes;

        const visibilityClasses = [
            visibility?.desktop ? 'hidden-desktop' : '',
            visibility?.tablet ? 'hidden-tablet' : '',
            visibility?.mobile ? 'hidden-mobile' : ''
        ].filter(Boolean);

        const wrapperProps = {
            className: ['wp-block-kanews-bibliography', 'kanews-post-sources', 'kanews-block-margin', ...visibilityClasses].filter(Boolean).join(' '),
            style: {
                ...getSpacingStyles(margin, 'margin')
            }
        };

        return (
            <div {...wrapperProps}>
                {sources.map((source, index) => 
                    source.url ? (
                        <div key={index} className='kanews-post-source d-flex justify-content-between align-items-center kanews-block-border' style={{...getBorderStyles(border, 'border')}}>
                            <a 
                                href={source.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={'kanews-source-link kanews-block-padding'}
                                style={{...linkTypography, ...getSpacingStyles(padding, 'padding')}}
                            >
                                {source.icon && <i className={source.icon}></i>}
                                <span className="kanews-source-text">{source.text}</span>
                            </a>
                        </div>
                    ) : (
                        <div key={index} className='kanews-post-source d-flex align-items-center kanews-block-border' style={{...getBorderStyles(border, 'border')}}>
                            <div className='kanews-source-link kanews-block-padding' style={{...linkTypography, ...getSpacingStyles(padding, 'padding')}}>
                                {source.icon && <i className={source.icon}></i>}
                                <span className="kanews-source-text">{source.text}</span>
                            </div>
                        </div>
                    )
                )}
            </div>
        );
    },
}); 